import { Location } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';

import { tap } from 'rxjs';
import { QueryStringTypes } from 'src/app/configs/query-string-types';
import {
  ComparisonObjectsWithLogicalOperators,
  SortModel,
} from 'src/app/core/api/models';
import { RegularExpressions } from 'src/app/core/constants/regular-expressions';
import { TableColumn } from 'src/app/core/models/dynamic-table/table-column.model';
import { AppStateService } from 'src/app/core/services/app-state.service';
import { ConsoleService } from 'src/app/core/services/console.service';
import { DialogService } from 'src/app/core/services/dialog.service';
import { AppDomService } from 'src/app/core/services/dom.service';
import { DynamicTabService } from 'src/app/core/services/dynamic-tab.service';
import { DynamicTableService } from 'src/app/core/services/dynamic-table.service';
import { AppIntroService } from 'src/app/layout/components/app-intro/app-intro.service';
import { Orders } from '../../../core/models/dynamic-table/order.model';
import { RoutingStateService } from '../../../core/services/routing-state.service';
import { TableConfig } from './../../../core/models/dynamic-table/table.config.model';
import { FieldSearcherOutput } from './../field-searcher/field-searcher-event.model';
import { StickyPositionDirective } from './directive/sticky-position.directive';
import { TableColumnSettingsComponent } from './table-column-settings/table-column-settings.component';
import { TableColumnStateService } from './table-column-state.service';

@Component({
  selector: 'app-dynamic-table',
  templateUrl: './dynamic-table.component.html',
  styleUrls: ['./dynamic-table.component.scss'],
  standalone: false,
})
export class DynamicTableComponent {
  //#region properties
  location = inject(Location);
  routingState = inject(RoutingStateService);
  appState = inject(AppStateService);
  appIntroService = inject(AppIntroService);
  elementRef = inject(ElementRef);
  dialogService = inject(DialogService);
  dynamicTabService = inject(DynamicTabService);
  innerWidth: number;

  readonly scroller = viewChild<ElementRef>('scroller');
  readonly table = viewChild<ElementRef>('table');
  readonly tableRow = viewChild.required<ElementRef>('tableRow');

  @ViewChild(StickyPositionDirective)
  stickyPositionDirective: StickyPositionDirective;

  tableColCount = [];
  preloadingRowCount = [];
  sortObject: Orders[] = [];
  comparisonObjects: ComparisonObjectsWithLogicalOperators[] = [];
  queryString = '';
  rowHeight: number;
  pagesWithCustomization: string[] = [
    'app-reseller-outbox-messages',
    'app-current-user-outbox-messages',
    'app-base-outbox-messages',
    'app-reseller-send-queue',
    'app-user-outbox-messages',
    'app-users-outbox-messages',
    'app-current-user-send-queue',
    'app-user-send-queue',
    'app-users-send-queue',
  ];
  applyCustomization: boolean;
  pageActive: string;
  closeTab: boolean = false;

  @Input() tableWidth: string;
  @Input('config') config: TableConfig;
  @Input('loading') input_loading: boolean;
  @Input('isEmpty') input_isEmpty: boolean;
  @Input('hasError') input_hasError: boolean;
  @Input('showParent') showParent: boolean;
  @Input() showDoubleScroll: boolean = true;
  @Input() defaultFilters: any[] = [];
  @Input() items: any[] = [];

  @Output('sortObject') output_sortObject = new EventEmitter<SortModel[]>();
  @Output('comparisonObjects') output_comparisonObject = new EventEmitter<
    ComparisonObjectsWithLogicalOperators[]
  >();
  @Output('pageSize') output_pageSize = new EventEmitter<number>();
  @Output('pageNumber') output_pageNumber = new EventEmitter<number>();
  @Output('onSelectAll') output_onSelectAll = new EventEmitter<boolean>();
  @Output('clearQueryString') output_clearQueryString =
    new EventEmitter<string>();

  @HostListener('window:resize', ['$event'])
  onWindowResize() {
    this.checkOverflow();
    this.innerWidth = window.innerWidth;
  }

  scrollInterval: any;
  isLoading: boolean;
  textVisibleFilter: string = 'messages.hideSearch';
  isShowFilterBtn: boolean = true;
  clearFilters = false;
  setTdNameAttribute = false;
  isShowScrollBtns = signal<boolean>(false);
  //#endregion

  constructor(
    private dynamicTableService: DynamicTableService,
    private readonly cdr: ChangeDetectorRef,
    private renderer: Renderer2
  ) {
    effect(() => {
      this.pageActive = this.dynamicTabService.activeTab();
      this.closeTab = this.dynamicTabService.closeTab();

      this.checkIgnoreTableCustomization();
      if (this.applyCustomization || this.closeTab) {
        let timeOut = setTimeout(() => {
          this.stickyPositionDirective.calculateAndSetStickyPosition();
          clearTimeout(timeOut);
        }, 50);
      }
    });
  }

  ngOnInit() {
    this.preloadingRowCount = [
      ...Array(this.config?.preloadingRowCount).keys(),
    ];

    this.isShowFilterBtn = this.config.tableColumns.some(
      (col) => col.hasSearch
    );

    if (this.routingState.isNotExceptedPages()) {
      // agar karbar url ba querystring vared kard bayad filter va sort ra az url bekhanim
      if (window.location.search) {
        let url = new URL(window.location.href);
        for (const item of url.searchParams.entries()) {
          if (item[0].startsWith('sort')) {
            this.getSortItems(
              { columnName: item[1], sort: +item[0].split('_')[1] },
              true
            );
          }
        }
        let filterObjects: FieldSearcherOutput[] = this.convertFilters();
        for (const object of filterObjects) {
          this.getComparisonObject(object, true);
        }
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.routingState.isNotExceptedPages())
      this.config.showDeleteFilters = false;

    this.queryString = window.location.search
      .replace(RegularExpressions.urlPageNumber, '')
      .replace(RegularExpressions.urlPageSize, '')
      .replace(RegularExpressions.urlChildKey, '');

    this.defaultFilters
      .filter((a) => a.field != undefined)
      .forEach((filter) => {
        // Create a dynamic regular expression to match the query parameter more flexibly
        const regexPattern = RegularExpressions.urlDefaultFilters(filter);
        const regex = new RegExp(regexPattern);
        // Replace the matching part with an empty string
        this.queryString = this.queryString.replace(regex, '');
      });
    this.queryString = this.queryString.replace('?', '').replaceAll('&', '');

    if (changes['input_loading'])
      if (this.input_loading)
        document
          .querySelectorAll('message-popover')
          .forEach((el) => el.remove());

    if (this.input_loading || this.input_isEmpty) {
      const stickyItems = document.querySelectorAll('.sticky-item');

      stickyItems.forEach((item) => {
        this.renderer.removeStyle(item, 'left');
        this.renderer.removeStyle(item, 'right');
      });
    }

    if (this.stickyPositionDirective && this.applyCustomization) {
      this.stickyPositionDirective.calculateAndSetStickyPosition();
      this.loadColumnData();
      const timeout = setTimeout(() => {
        this.stickyPositionDirective.calculateAndSetStickyPosition();
        clearTimeout(timeout);
      }, 100);
    }
  }

  convertFilters(query = '') {
    let params = {};
    if (query == '') params = this.routingState.parseQueryString();
    else params = this.routingState.parseQueryString(query);

    const result = [];
    const tempMap = new Map();

    for (const [key, value] of Object.entries(params)) {
      if (
        key.toLowerCase() != QueryStringTypes.PageNumber &&
        key.toLowerCase() != QueryStringTypes.PageSize &&
        !key.startsWith(QueryStringTypes.Sort) &&
        !key.startsWith('key-')
      ) {
        const [field, operator, logicalOperator] = key.split('_');
        const operatorInt = parseInt(operator, 10);
        const logicalOperatorInt =
          logicalOperator !== undefined ? parseInt(logicalOperator, 10) : null;

        if (logicalOperatorInt !== null) {
          const entryKey = `${logicalOperatorInt}_${field}`;
          if (!tempMap.has(entryKey)) {
            tempMap.set(entryKey, {
              fieldName: field,
              comparisonObjects: [],
              logicalComparisonOperator: logicalOperatorInt,
            });
          }
          tempMap.get(entryKey).comparisonObjects.push({
            field: field,
            operator: operatorInt,
            value: value,
          });
        } else {
          result.push({
            comparisonObjects: [
              {
                field: field,
                operator: operatorInt,
                value: value.toString(),
              },
            ],
          });
        }
      }
    }

    for (const entry of tempMap.values()) {
      // if(window.location.search.includes())
      result.push(entry);
    }
    return result;
  }

  ngAfterViewChecked(): void {
    const element = document.querySelector(
      `${this.config.pageTagName} .dynamic-table`
    );
    const width = element?.scrollWidth + 'px';
    this.tableWidth = width;

    this.checkOverflow();
    this.innerWidth = window.innerWidth;
  }

  onChangeTableScrollListener() {
    const tableElement = document.querySelector(
      `${this.config.pageTagName} .card-body`
    );
    tableElement.addEventListener('overflow', (e) => {
      ConsoleService.info('event', e);
    });
  }
  //#region sort
  getSortItems(sortItem: SortModel, isOnLoad: boolean = false) {
    this.sortObject = this.dynamicTableService.checkSortObject(
      this.sortObject,
      sortItem
    );
    if (!isOnLoad) this.emitSort();
  }
  //#endregion

  //#region pagination
  emitPageSize(pageSize) {
    this.output_pageSize.emit(pageSize);
  }
  emitPageNumber(pageNumber) {
    this.output_pageNumber.emit(pageNumber);
  }
  //#endregion

  //#region filter
  cleanData(data) {
    return data
      .map((parentObj) => {
        const filteredComparisonObjects = parentObj.comparisonObjects.filter(
          (obj) =>
            Object.values(obj).every(
              (value) =>
                value !== undefined &&
                value !== null &&
                value !== '' &&
                value !== 'undefined'
            )
        );
        return { ...parentObj, comparisonObjects: filteredComparisonObjects };
      })
      .filter((parentObj) => parentObj.comparisonObjects.length > 0);
  }

  getComparisonObject(event: FieldSearcherOutput, isOnLoad: boolean = false) {
    let url = window.location.pathname + window.location.search;
    if (window.location.search.includes('key-')) {
      let params = this.routingState.parseQueryString();
      let childUrl;
      for (const [key, value] of Object.entries(params)) {
        if (key.startsWith('key-')) childUrl = '?' + key + '=' + value;

        this.location.go(window.location.pathname + childUrl);
      }
    }
    const fieldName = event.fieldName;
    delete event.fieldName;
    this.comparisonObjects = [...this.cleanData(this.comparisonObjects)];
    this.comparisonObjects =
      this.dynamicTableService.addOrRemoveComparisonObject(
        this.comparisonObjects,
        event,
        fieldName
      );

    this.location.go(url);
    if (!isOnLoad) this.emitSearch();
  }
  //#endregion

  emitSort() {
    this.output_sortObject.emit(this.sortObject);
  }

  emitSearch() {
    this.output_comparisonObject.emit(this.comparisonObjects);
  }

  clearQueryString() {
    // پاک نشدن کلیدها توسط دکمه حذف فیلترها
    let key2 = '';
    if (!this.showParent) {
      let params = this.routingState.parseQueryString();
      for (const [key, value] of Object.entries(params)) {
        if (key.startsWith('key-')) {
          key2 = '?' + key + '=' + value;
        }
      }
    }
    this.location.go(window.location.pathname + key2);
    this.sortObject = [];
    this.comparisonObjects = [];
    this.output_clearQueryString.emit('');
    this.clearFilters = this.clearFilters == true ? false : true;
  }

  emitSelectAll(event) {
    let checked = event?.target?.checked;
    this.output_onSelectAll.emit(checked);
  }

  //#region scroll
  doubleScroll(): void {
    this.table()?.nativeElement.scrollTo(
      this.scroller().nativeElement.scrollLeft,
      0
    );
  }
  TableScroll(): void {
    this.scroller()?.nativeElement.scrollTo(
      this.table().nativeElement.scrollLeft,
      0
    );
  }

  // وقتی روی دکمه اسکرول نگه میداریم
  startScrollLeft() {
    this.scrollInterval = setInterval(() => {
      this.scrollLeft();
    }, 200); //  زمان اسکرول
  }

  startScrollRight() {
    this.scrollInterval = setInterval(() => {
      this.scrollRight();
    }, 200); //  زمان اسکرول
  }

  stopScroll() {
    clearInterval(this.scrollInterval);
  }

  scrollLeft() {
    this.scroller().nativeElement.scrollLeft -= 100; // تعداد پیکسل‌هایی که می‌خواهید به چپ اسکرول کند
  }

  scrollRight() {
    this.scroller().nativeElement.scrollLeft += 100; // تعداد پیکسل‌هایی که می‌خواهید به راست اسکرول کند
  }

  checkOverflow() {
    const scroller = this.scroller();
    if (scroller) {
      const scrollBar: HTMLElement = scroller.nativeElement;
      const curOverflow: boolean =
        scrollBar.scrollWidth > scrollBar.clientWidth;
      this.isShowScrollBtns.set(curOverflow);

      this.rowHeight = this.tableRow().nativeElement.offsetHeight;
      this.cdr.markForCheck();
    }
  }
  //#endregion

  openTableColumnSettingsDialog() {
    const filteredColumns = this.config.tableColumns.map(
      ({ title, name, order, isHide, isSticky }) => ({
        title,
        name,
        order,
        isHide,
        isSticky,
      })
    );

    const modalResult = this.dialogService.openModal({
      component: TableColumnSettingsComponent,
      data: { columns: filteredColumns, pageName: this.config?.pageTagName },
    });
    modalResult
      .afterClosed()
      .pipe(
        // takeUntil(this.onDestroy$),
        tap(() => {
          AppDomService.removeModalScrollClassFromHtml();
        })
      )
      .subscribe((res) => {
        if (res && res.length) {
          this.input_loading = true;
          this.clearQueryString();
          // به‌روزرسانی `config.tableColumns` با مقدار جدید
          this.config.tableColumns = this.config.tableColumns.map((column) => {
            const updatedColumn = res.find(
              (item) => item.name === column?.name
            );
            return updatedColumn ? { ...column, ...updatedColumn } : column;
          });

          if (this.input_loading)
            document
              .querySelectorAll('message-popover')
              .forEach((el) => el.remove());

          if (this.input_loading || this.input_isEmpty) {
            const stickyItems = document.querySelectorAll('.sticky-item');

            stickyItems.forEach((item) => {
              this.renderer.removeStyle(item, 'left');
              this.renderer.removeStyle(item, 'right');
            });
          }

          let timeOut = setTimeout(() => {
            this.input_loading = false;
            clearTimeout(timeOut);
          }, 2000);
        }
      });
  }

  resetTableSettings(): void {
    TableColumnStateService.resetData(this.config.pageTagName);
    this.config.tableColumns = this.config.tableColumns.map((item) => {
      const { isHide, order, size, ...rest } = item;
      return rest;
    });
    this.clearQueryString();
  }

  setColumnSize(size: number, column: TableColumn) {
    if (this.config?.tableColumns)
      this.config.tableColumns = this.config.tableColumns.map((col) => {
        if (col.name === column?.name)
          return {
            ...col,
            size: size,
          };

        return col;
      });

    TableColumnStateService.setData(
      this.config.pageTagName,
      column?.name,
      'size',
      size
    );
  }

  loadColumnData() {
    this.checkIgnoreTableCustomization();
    if (!this.applyCustomization) return;
    const columnData = TableColumnStateService.getData(this.config.pageTagName);
    if (!columnData) return;
    this.config.tableColumns = this.config.tableColumns.map((column) => {
      const columnState = columnData[column?.name];
      if (columnState)
        return {
          ...column,
          ...columnState,
        };

      return column;
    });
  }

  checkIgnoreTableCustomization() {
    this.applyCustomization = this.pagesWithCustomization.includes(
      this.config.pageTagName
    );
  }
}
